import type { AppLocale } from "../i18n/locales";
import { DEFAULT_LOCALE } from "../i18n/locales";
import { absoluteLocalizedUrl } from "../i18n/seoUrls";
import { TrendingHottestItem } from "../shared/type";
import { trendingItemPathFromItem } from "./trendingItemId";

export function trendingItemFallbackPath(productId: string | undefined): string {
  return productId ? `/trendingItem/${productId}` : "/trendingItem";
}

export function trendingItemCanonicalPath(item: TrendingHottestItem): string {
  return trendingItemPathFromItem(item);
}

export function trendingItemDescription(item: TrendingHottestItem): string {
  const pricePart = item.minPriceFormat
    ? ` starting at ${item.minPriceFormat}`
    : "";
  return `Discover ${item.name}, a trending trading card game product${pricePart}. Browse price trends and more hot TCG picks on FindPoketCGStore.`;
}

export function trendingItemKeywords(item: TrendingHottestItem): string {
  return [
    item.name,
    "trending TCG cards",
    "trading card game",
    "card market prices",
    "SNKRDUNK trending",
    "Pokemon cards",
    "One Piece cards",
  ].join(", ");
}

export function trendingItemStructuredData(
  item: TrendingHottestItem,
  otherItems: TrendingHottestItem[],
  path: string,
  locale: AppLocale = DEFAULT_LOCALE,
): Array<Record<string, unknown>> {
  const pageUrl = absoluteLocalizedUrl(path, locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const product: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.name,
    image: item.thumbnailUrl,
    description: trendingItemDescription(item),
    url: pageUrl,
    category: "Trading Card Game",
  };

  if (item.minPrice > 0 && item.minPriceFormat) {
    product.offers = {
      "@type": "Offer",
      price: item.minPrice,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: pageUrl,
    };
  }

  if (item.productNumber) {
    product.sameAs = item.productNumber;
  }

  const structuredData: Array<Record<string, unknown>> = [
    product,
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: homeUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Trending TCG",
          item: homeUrl,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: item.name,
          item: pageUrl,
        },
      ],
    },
  ];

  if (otherItems.length > 0) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "More Trending TCG Products",
      numberOfItems: otherItems.length,
      itemListElement: otherItems.map((entry, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: entry.name,
        url: absoluteLocalizedUrl(trendingItemCanonicalPath(entry), locale),
      })),
    });
  }

  return structuredData;
}

export function buildTrendingItemSeo(
  item: TrendingHottestItem,
  otherItems: TrendingHottestItem[],
  locale: AppLocale = DEFAULT_LOCALE,
) {
  const path = trendingItemCanonicalPath(item);

  return {
    title: item.name,
    description: trendingItemDescription(item),
    path,
    imageUrl: item.thumbnailUrl,
    keywords: trendingItemKeywords(item),
    structuredData: trendingItemStructuredData(
      item,
      otherItems,
      path,
      locale,
    ),
  };
}
